/**
 * Criptografía asimétrica del kit: identidad por usuario (par de claves ECDH
 * P-256) y "sellado a clave pública" estilo ECIES.
 *
 * Uso: repartir la clave de un vault de organización a cada miembro (se cifra
 * la clave del vault a la CLAVE PÚBLICA del miembro; solo su clave privada la
 * abre) y compartir un secreto puntual a un destinatario. El emisor no necesita
 * la clave privada del receptor: privacidad perfecta hacia adelante por sobre
 * gracias a un par efímero por operación.
 *
 * ECIES(P-256):
 *   epk = par efímero;  shared = ECDH(epk.priv, recipient.pub)
 *   wrapKey = HKDF(shared, info = "…" || epk.pub)   (ata la wrap key al epk)
 *   sobre = AES-256-GCM(wrapKey, payload, aad)
 *   se transmite { epk.pub, sobre };  el receptor recomputa shared con su priv.
 */

import { base64ToBytes, bytesToBase64, concatBytes } from "./encoding";
import { aeadOpen, aeadSeal, hkdfExpand, importAesKey } from "./primitives";

const CURVE = "P-256";

export interface KeyPairRaw {
  /** Clave pública en formato raw (65 bytes, base64). Pública: no es secreto. */
  readonly publicKey: string;
  /** Clave privada en PKCS#8 (base64). SECRETA: se envuelve con la clave de cuenta. */
  readonly privateKey: string;
}

export interface SealedToPublicKey {
  /** Clave pública efímera (raw, base64). */
  readonly epk: string;
  /** Sobre AES-GCM (base64). */
  readonly env: string;
}

function ab(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.length);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export async function generateKeyPair(): Promise<KeyPairRaw> {
  const pair = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: CURVE }, true, [
    "deriveBits",
  ]);
  const pub = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
  const priv = new Uint8Array(await crypto.subtle.exportKey("pkcs8", pair.privateKey));
  return { publicKey: bytesToBase64(pub), privateKey: bytesToBase64(priv) };
}

async function importPublic(raw: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", ab(raw), { name: "ECDH", namedCurve: CURVE }, true, []);
}

async function importPrivate(pkcs8: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("pkcs8", ab(pkcs8), { name: "ECDH", namedCurve: CURVE }, false, [
    "deriveBits",
  ]);
}

async function ecdh(priv: CryptoKey, pub: CryptoKey): Promise<Uint8Array> {
  const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: pub }, priv, 256);
  return new Uint8Array(bits);
}

/** Sella `payload` (típicamente una clave de vault, 32 bytes) a `publicKeyB64`. */
export async function sealToPublicKey(
  publicKeyB64: string,
  payload: Uint8Array,
  aad: string,
): Promise<SealedToPublicKey> {
  const recipientPub = await importPublic(base64ToBytes(publicKeyB64));
  const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: CURVE }, true, [
    "deriveBits",
  ]);
  const epkRaw = new Uint8Array(await crypto.subtle.exportKey("raw", ephemeral.publicKey));
  const shared = await ecdh(ephemeral.privateKey, recipientPub);
  const wrapRaw = await hkdfExpand(concatBytes(shared, epkRaw), `boveda:ecies:v1:${aad}`);
  const wrapKey = await importAesKey(wrapRaw, ["encrypt"]);
  const env = await aeadSeal(wrapKey, payload, aad);
  return { epk: bytesToBase64(epkRaw), env: bytesToBase64(env) };
}

export async function openWithPrivateKey(
  privateKeyB64: string,
  sealed: SealedToPublicKey,
  aad: string,
): Promise<Uint8Array> {
  const priv = await importPrivate(base64ToBytes(privateKeyB64));
  const epkRaw = base64ToBytes(sealed.epk);
  const epk = await importPublic(epkRaw);
  const shared = await ecdh(priv, epk);
  const wrapRaw = await hkdfExpand(concatBytes(shared, epkRaw), `boveda:ecies:v1:${aad}`);
  const wrapKey = await importAesKey(wrapRaw, ["decrypt"]);
  return aeadOpen(wrapKey, base64ToBytes(sealed.env), aad);
}
